
/** Every path in one place, so no route string is written twice. */
export const ROUTES = {
  login: '/login',
  dashboard: '/',
  /** The figures behind the launcher: every module's KPIs and trends. */
  insights: '/insights',
  notifications: '/notifications',
  schools: '/schools',
  campuses: '/campuses',
  academicYears: '/academic-years',
  users: '/users',
  roles: '/roles',
  permissions: '/permissions',
  grades: '/grades',
  gradeOfferings: '/grade-offerings',
  sections: '/sections',
  subjects: '/subjects',
  students: '/students',
  studentDetail: (id = ':id') => `/students/${id}`,
  enrollments: '/enrollments',
  guardians: '/guardians',
  guardianDetail: (id = ':id') => `/guardians/${id}`,
  promotion: '/promotion',
  admissions: '/admissions',
  admissionDetail: (id = ':id') => `/admissions/${id}`,

  // ---- notices and oversight ---------------------------------------------
  announcements: '/announcements',
  myAnnouncements: '/my/announcements',
  auditTrail: '/audit',
  dataImport: '/import',

  employees: '/employees',
  teachers: '/teachers',
  curriculum: '/curriculum',
  teacherAssignments: '/teaching-assignments',
  periods: '/periods',
  calendar: '/calendar',
  timetables: '/timetables',
  attendance: '/attendance',
  staffAttendance: '/staff-attendance',
  assignments: '/assignments',

  examinations: '/examinations',
  examinationDetail: (id = ':id') => `/examinations/${id}`,
  examinationMarks: (id = ':id', paperId = ':paperId') => `/examinations/${id}/papers/${paperId}/marks`,
  examinationResults: (id = ':id') => `/examinations/${id}/results`,
  reportCard: (id = ':id', studentId = ':studentId') =>
    `/examinations/${id}/results/${studentId}/report-card`,

  financeDashboard: '/finance',
  financeSetup: '/finance/setup',
  feeStructures: '/finance/structures',
  feeAssignments: '/finance/assignments',
  invoices: '/finance/invoices',
  invoiceDetail: (id = ':id') => `/finance/invoices/${id}`,
  payments: '/finance/payments',
  paymentDetail: (id = ':id') => `/finance/payments/${id}`,
  receipt: (id = ':id') => `/finance/payments/${id}/receipt`,
  discounts: '/finance/discounts',
  scholarships: '/finance/scholarships',
  refunds: '/finance/refunds',
  financeReports: '/finance/reports',

  // ---- HR -----------------------------------------------------------------
  hrDashboard: '/hr',
  hrSettings: '/hr/settings',
  leaveRequests: '/hr/leave',
  leaveTypes: '/hr/leave/types',
  leaveBalances: '/hr/leave/balances',
  myLeave: '/my/leave',
  salaryComponents: '/hr/salary/components',
  salaryStructures: '/hr/salary/structures',
  salaryAssignments: '/hr/salary/assignments',
  payroll: '/hr/payroll',
  payrollDetail: (id = ':id') => `/hr/payroll/${id}`,
  payslip: (id = ':id') => `/hr/payslips/${id}`,
  myPayslips: '/my/payslips',
  loans: '/hr/loans',
  hrReports: '/hr/reports',

  // ---- library ------------------------------------------------------------
  libraryDashboard: '/library',
  circulationDesk: '/library/desk',
  catalogue: '/library/catalogue',
  bookDetail: (id = ':id') => `/library/catalogue/${id}`,
  libraryMembers: '/library/members',
  libraryMemberDetail: (id = ':id') => `/library/members/${id}`,
  loansList: '/library/loans',
  overdueLoans: '/library/loans/overdue',
  reservations: '/library/reservations',
  libraryFines: '/library/fines',
  lostAndDamaged: '/library/lost-damaged',
  libraryInventory: '/library/inventory',
  inventoryAudit: (id = ':id') => `/library/inventory/audits/${id}`,
  acquisitions: '/library/acquisitions',
  acquisitionDetail: (id = ':id') => `/library/acquisitions/${id}`,
  librarySuppliers: '/library/suppliers',
  libraryCategories: '/library/categories',
  librarySettings: '/library/settings',
  libraryReports: '/library/reports',

  // What a reader does for themselves.
  browseCatalogue: '/my/library',

  // ---- transport ----------------------------------------------------------
  transportDashboard: '/transport',
  transportOperations: '/transport/operations',
  vehicles: '/transport/vehicles',
  vehicleDetail: (id = ':id') => `/transport/vehicles/${id}`,
  transportDrivers: '/transport/drivers',
  transportDriverDetail: (id = ':id') => `/transport/drivers/${id}`,
  transportRoutes: '/transport/routes',
  transportRouteDetail: (id = ':id') => `/transport/routes/${id}`,
  transportEnrollments: '/transport/enrollments',
  trips: '/transport/trips',
  tripDetail: (id = ':id') => `/transport/trips/${id}`,
  transportIncidents: '/transport/incidents',
  transportMaintenance: '/transport/maintenance',
  transportReports: '/transport/reports',
  transportSettings: '/transport/settings',

  // What a driver does for themselves.
  myTransport: '/my/transport',

  // ---- student & parent portal ---------------------------------------------
  // A guardian's pages take ?child=<studentId>; a student's are always their own.
  myChildren: '/my/children',
  myOverview: '/my/overview',
  myAttendance: '/my/attendance',
  myTimetable: '/my/timetable',
  myResults: '/my/results',
  myResult: (examinationId = ':examinationId') => `/my/results/${examinationId}`,
  myFees: '/my/fees',
  myReceipt: (paymentId = ':paymentId') => `/my/fees/receipts/${paymentId}`,
  // Not /my/transport: that is the driver's page.
  myBus: '/my/bus',
  myLibraryAccount: '/my/library-account',
  myAssignments: '/my/assignments',

  // A teacher's own week.
  myTeaching: '/my/teaching',

  // ---- assets & stock -----------------------------------------------------
  assets: '/assets',
  assetDetail: (id = ':id') => `/assets/${id}`,
  assetAudits: '/assets/audits',
  assetAuditDetail: (id = ':id') => `/assets/audits/${id}`,
  stock: '/assets/stock',
  assetSetup: '/assets/setup',

  forbidden: '/forbidden',
} as const;
